import React, { useEffect, useState } from 'react';
import { ExternalLink, Pencil, Plus, Trash2 } from 'lucide-react';
import {
  createProductAdmin,
  deleteProductAdmin,
  listProductsAdmin,
  productInputFromRow,
  setProductPublishedAdmin,
  updateProductAdmin,
  type ProductInput,
  type ProductRow,
} from '../../lib/products';
import { categoryLabels, type ProductCategory } from '../../data/products';
import { marketingHref } from '../../lib/surface';
import { EmptyState, Panel, PanelHead, Stamp, fieldCls, labelCls } from '../../components/lms/ui';
import { ListRowsSkeleton } from '../../components/lms/Skeletons';

const emptyForm: ProductInput = {
  title: '',
  description: '',
  category: 'short-course',
  price_value: 0,
  image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80',
  hours: null,
  students: null,
  rating: null,
  author: 'LSEM Faculty',
  downloads: null,
  download_url: null,
  whatsapp_link: null,
  is_published: false,
  sort_order: 0,
};

const ProductsAdmin: React.FC = () => {
  const [items, setItems] = useState<ProductRow[]>([]);
  const [form, setForm] = useState<ProductInput>(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  async function load() {
    setLoading(true);
    try {
      const rows = await listProductsAdmin();
      setItems(rows);
      return rows;
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load().catch((err) => setError(err instanceof Error ? err.message : 'Could not load products.')); }, []);

  function update<K extends keyof ProductInput>(key: K, value: ProductInput[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  const numberOrNull = (value: string) => (value.trim() === '' ? null : Number(value));

  const startCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm, sort_order: items.length });
    setError('');
    setNotice('');
  };

  const startEdit = (row: ProductRow) => {
    setEditingId(row.id);
    setForm(productInputFromRow(row));
    setError('');
    setNotice('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.title.trim()) {
      setError('A product title is required.');
      return;
    }

    setBusy('save');
    setError('');
    setNotice('');
    try {
      const payload: ProductInput = {
        ...form,
        title: form.title.trim(),
        description: form.description.trim(),
        price_value: Number(form.price_value) || 0,
        image: form.image.trim(),
        author: form.author?.trim() || null,
        download_url: form.download_url?.trim() || null,
        whatsapp_link: form.whatsapp_link?.trim() || null,
        downloads: form.downloads?.trim() || null,
        sort_order: Number(form.sort_order) || 0,
      };

      const id = editingId
        ? (await updateProductAdmin(editingId, payload), editingId)
        : await createProductAdmin(payload);

      await load();
      setEditingId(id);
      setNotice(editingId ? 'Product updated.' : 'Product added to the catalogue.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the product.');
    } finally {
      setBusy('');
    }
  };

  const togglePublish = async (row: ProductRow) => {
    setBusy(`publish-${row.id}`);
    setError('');
    setNotice('');
    try {
      await setProductPublishedAdmin(row.id, !row.is_published);
      await load();
      setNotice(row.is_published ? 'Product moved to draft.' : 'Product published.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not change publication.');
    } finally {
      setBusy('');
    }
  };

  const remove = async (row: ProductRow) => {
    if (!window.confirm(`Remove “${row.title}” from the catalogue? This cannot be undone.`)) return;
    setBusy(`delete-${row.id}`);
    setError('');
    setNotice('');
    try {
      await deleteProductAdmin(row.id);
      if (editingId === row.id) startCreate();
      await load();
      setNotice('Product removed.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove the product.');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] xl:items-start">
      <Panel>
        <PanelHead
          title="Short course catalogue"
          action={
            <div className="flex items-center gap-3">
              <a
                href={marketingHref('/short-courses')}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.1em] text-blue-700 hover:text-blue-800"
              >
                Public catalogue <ExternalLink className="h-3 w-3" strokeWidth={1.7} />
              </a>
              <button type="button" onClick={startCreate} className="lms-btn-secondary !px-3 !py-2 !text-[11px]">
                <Plus className="h-3.5 w-3.5" strokeWidth={1.8} /> New
              </button>
            </div>
          }
        />

        <div className="border-b border-rule bg-paper-2 px-5 py-3 text-[11.5px] leading-5 text-ink-2">
          Short course products are sold through Paystack, download or WhatsApp. They do not provision LMS programme
          access; programme enrolment is managed under Programmes.
        </div>

        {error && <p className="mx-5 mt-4 border border-danger/25 bg-danger-soft px-3 py-2 text-[12px] text-danger" role="alert">{error}</p>}
        {notice && <p className="mx-5 mt-4 border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12px] text-emerald-800" role="status">{notice}</p>}

        {loading ? (
          <ListRowsSkeleton rows={4} label="Loading products" />
        ) : items.length === 0 ? (
          <EmptyState
            title="No products in the catalogue"
            body="Add a short course, e-book, WhatsApp class or free resource. Published products appear immediately on the public catalogue."
            action={<button type="button" onClick={startCreate} className="lms-btn-primary !px-4 !py-2 !text-[12px]"><Plus className="h-3.5 w-3.5" strokeWidth={1.8} /> Add product</button>}
          />
        ) : (
          <ul className="divide-y divide-rule">
            {items.map((row) => (
              <li key={row.id} className={`px-5 py-4 transition-colors ${editingId === row.id ? 'bg-blue-50/60' : ''}`}>
                <div className="flex items-start gap-3">
                  <div className="h-14 w-16 flex-none overflow-hidden rounded-lg border border-rule bg-paper-2">
                    {row.image ? (
                      <img src={row.image} alt="" className="h-full w-full object-cover" loading="lazy" />
                    ) : (
                      <div className="flex h-full items-center justify-center font-mono text-[8px] uppercase tracking-[0.1em] text-ink-2">
                        No image
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Stamp tone={row.is_published ? 'cleared' : 'neutral'}>{row.is_published ? 'Published' : 'Draft'}</Stamp>
                      <span className="font-mono text-[8.5px] uppercase tracking-[0.1em] text-ink-2">
                        {categoryLabels[row.category as ProductCategory] ?? row.category}
                      </span>
                    </div>
                    <p className="mt-1.5 truncate text-[13.5px] font-semibold text-ink">{row.title}</p>
                    <p className="mt-0.5 line-clamp-1 text-[11.5px] text-ink-2">{row.description}</p>
                    <p className="mt-1.5 font-mono text-[9px] uppercase tracking-[0.08em] text-ink-2">
                      {Number(row.price_value) === 0 ? 'Free' : `₦${Number(row.price_value).toLocaleString('en-NG')}`}
                      {' · '}sort {row.sort_order}
                    </p>
                  </div>
                  <div className="flex flex-none items-center gap-1">
                    <button
                      type="button"
                      onClick={() => startEdit(row)}
                      className="flex h-8 w-8 items-center justify-center rounded-lg text-ink-2 transition-colors hover:bg-blue-50 hover:text-blue-700"
                      aria-label={`Edit ${row.title}`}
                      title="Edit product"
                    >
                      <Pencil className="h-3.5 w-3.5" strokeWidth={1.6} />
                    </button>
                    <button
                      type="button"
                      onClick={() => void togglePublish(row)}
                      disabled={busy === `publish-${row.id}`}
                      className="rounded-lg border border-rule px-2 py-1.5 font-mono text-[8px] uppercase tracking-[0.08em] text-ink-2 transition-colors hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700 disabled:opacity-50"
                    >
                      {row.is_published ? 'Unpublish' : 'Publish'}
                    </button>
                    <button
                      type="button"
                      onClick={() => void remove(row)}
                      disabled={busy === `delete-${row.id}`}
                      className="flex h-8 w-8 items-center justify-center rounded-lg text-ink-2 transition-colors hover:bg-danger-soft hover:text-danger disabled:opacity-50"
                      aria-label={`Delete ${row.title}`}
                      title="Remove product"
                    >
                      <Trash2 className="h-3.5 w-3.5" strokeWidth={1.6} />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel>
        <PanelHead
          title={editingId ? 'Edit product' : 'Add product'}
          action={<span className="font-mono text-[9px] uppercase tracking-[0.1em] text-ink-2">{editingId ? editingId : 'New entry'}</span>}
        />
        <form onSubmit={submit} className="space-y-4 px-5 py-4">
          <div>
            <label htmlFor="product-title" className={labelCls}>Title</label>
            <input
              id="product-title"
              className={fieldCls}
              value={form.title}
              onChange={(event) => update('title', event.target.value)}
              placeholder="e.g. Export Documentation Essentials"
              required
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="product-category" className={labelCls}>Type</label>
              <select
                id="product-category"
                className={fieldCls}
                value={form.category}
                onChange={(event) => update('category', event.target.value as ProductCategory)}
              >
                {(Object.keys(categoryLabels) as ProductCategory[]).map((category) => (
                  <option key={category} value={category}>{categoryLabels[category]}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="product-price" className={labelCls}>Price (₦)</label>
              <input
                id="product-price"
                type="number"
                min={0}
                step={1000}
                className={fieldCls}
                value={form.price_value}
                onChange={(event) => update('price_value', Number(event.target.value) || 0)}
              />
            </div>
          </div>

          <div>
            <label htmlFor="product-description" className={labelCls}>Description</label>
            <textarea
              id="product-description"
              className={fieldCls}
              rows={4}
              value={form.description}
              onChange={(event) => update('description', event.target.value)}
              placeholder="What the student receives and who it is for…"
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="product-hours" className={labelCls}>Hours</label>
              <input
                id="product-hours"
                type="number"
                min={0}
                className={fieldCls}
                value={form.hours ?? ''}
                onChange={(event) => update('hours', numberOrNull(event.target.value))}
                placeholder="e.g. 6"
              />
            </div>
            <div>
              <label htmlFor="product-students" className={labelCls}>Students</label>
              <input
                id="product-students"
                type="number"
                min={0}
                className={fieldCls}
                value={form.students ?? ''}
                onChange={(event) => update('students', numberOrNull(event.target.value))}
                placeholder="e.g. 240"
              />
            </div>
            <div>
              <label htmlFor="product-rating" className={labelCls}>Rating (0–5)</label>
              <input
                id="product-rating"
                type="number"
                min={0}
                max={5}
                step={0.1}
                className={fieldCls}
                value={form.rating ?? ''}
                onChange={(event) => update('rating', numberOrNull(event.target.value))}
                placeholder="e.g. 4.8"
              />
            </div>
            <div>
              <label htmlFor="product-sort" className={labelCls}>Sort order</label>
              <input
                id="product-sort"
                type="number"
                className={fieldCls}
                value={form.sort_order}
                onChange={(event) => update('sort_order', Number(event.target.value) || 0)}
              />
            </div>
          </div>

          <div>
            <label htmlFor="product-image" className={labelCls}>Image URL</label>
            <input
              id="product-image"
              type="url"
              className={fieldCls}
              value={form.image}
              onChange={(event) => update('image', event.target.value)}
              placeholder="https://…"
            />
            {form.image && (
              <div className="mt-2 h-24 w-full overflow-hidden rounded-lg border border-rule bg-paper-2">
                <img src={form.image} alt="" className="h-full w-full object-cover" />
              </div>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="product-author" className={labelCls}>Author / faculty</label>
              <input
                id="product-author"
                className={fieldCls}
                value={form.author ?? ''}
                onChange={(event) => update('author', event.target.value || null)}
                placeholder="LSEM Faculty"
              />
            </div>
            <div>
              <label htmlFor="product-downloads" className={labelCls}>Downloads label</label>
              <input
                id="product-downloads"
                className={fieldCls}
                value={form.downloads ?? ''}
                onChange={(event) => update('downloads', event.target.value || null)}
                placeholder="e.g. 1.2K"
              />
            </div>
          </div>

          <div>
            <label htmlFor="product-download-url" className={labelCls}>Download URL</label>
            <input
              id="product-download-url"
              type="url"
              className={fieldCls}
              value={form.download_url ?? ''}
              onChange={(event) => update('download_url', event.target.value || null)}
              placeholder="For free e-books and resources"
            />
          </div>

          <div>
            <label htmlFor="product-whatsapp" className={labelCls}>WhatsApp link</label>
            <input
              id="product-whatsapp"
              type="url"
              className={fieldCls}
              value={form.whatsapp_link ?? ''}
              onChange={(event) => update('whatsapp_link', event.target.value || null)}
              placeholder="https://wa.me/…"
            />
          </div>

          <label htmlFor="product-publish" className="flex items-center gap-2.5 text-[12.5px] text-ink">
            <input
              id="product-publish"
              type="checkbox"
              checked={form.is_published}
              onChange={(event) => update('is_published', event.target.checked)}
              className="h-4 w-4 rounded-none border-rule accent-pine"
            />
            Publish on the public catalogue
          </label>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-rule pt-4">
            <button
              type="button"
              onClick={startCreate}
              className="font-mono text-[9px] uppercase tracking-[0.1em] text-ink-2 hover:text-ink"
            >
              Clear form
            </button>
            <button type="submit" disabled={busy === 'save'} className="lms-btn-primary !px-4 !py-2 !text-[12px]">
              {busy === 'save' ? 'Saving…' : editingId ? 'Save changes' : 'Add product'}
            </button>
          </div>
        </form>
      </Panel>
    </div>
  );
};

export default ProductsAdmin;
